// CRM Company Intelligence — shared surface. Colourful rebuild of CompanyDetailView.
// layout="centre": stacked tabs (cockpit). layout="record": HubSpot-style header +
// two columns (Data Agent left / Buyer Intent right). One fetch, full digest workflow.
import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import API_BASE from '../../config';
import { creditAwareFetch } from '../../hooks/creditAwareFetch';
import { parseSectors } from '../../components/sectors';

const SIGNAL = {
  funding: { c: '#056F4E', bg: '#D1F0E4', l: 'Funding', icon: '$' },
  leadership_change: { c: '#5B3FD6', bg: '#E7E1FD', l: 'Leadership', icon: '★' },
  hiring: { c: '#0B5BB5', bg: '#D9E9FB', l: 'Hiring', icon: '↗' },
  tech_change: { c: '#066576', bg: '#CFF0F6', l: 'Tech', icon: '⚙' },
  news: { c: '#3F4956', bg: '#E3E7ED', l: 'News', icon: '◆' },
};
const SIGNAL_LABELS = { funding: 'Funding round', hiring: 'Hiring activity', leadership_change: 'Leadership change', news: 'Company news', tech_change: 'Technology change' };
const GREEN = '#00A878';

// Group signals by recency, matching the classic Intelligence feed.
function groupByDate(items) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterday = today - 86400000;
  const thisWeekStart = today - now.getDay() * 86400000;
  const lastWeekStart = thisWeekStart - 7 * 86400000;
  const g = { today: [], yesterday: [], thisWeek: [], lastWeek: [], earlier: [] };
  items.forEach(it => {
    const ds = it.detected_at || it.created_at;
    const t = ds ? new Date(ds).getTime() : 0;
    if (t >= today) g.today.push(it);
    else if (t >= yesterday) g.yesterday.push(it);
    else if (t >= thisWeekStart) g.thisWeek.push(it);
    else if (t >= lastWeekStart) g.lastWeek.push(it);
    else g.earlier.push(it);
  });
  return [
    { label: 'Today', items: g.today },
    { label: 'Yesterday', items: g.yesterday },
    { label: 'This week', items: g.thisWeek },
    { label: 'Last week', items: g.lastWeek },
    { label: 'Earlier', items: g.earlier }
  ].filter(s => s.items.length > 0);
}

function ukDate(v) { if (!v) return '—'; const d = new Date(v); return isNaN(d) ? '—' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
function relTime(v) { if (!v) return ''; const d = new Date(v); if (isNaN(d)) return ''; const diff = Date.now() - d.getTime(); const day = 86400000; if (diff < day) return 'Today'; if (diff < 2 * day) return 'Yesterday'; if (diff < 30 * day) return Math.floor(diff / day) + ' days ago'; return ukDate(v); }
function favDomain(website) { return website ? String(website).replace(/^https?:\/\/(www\.)?/, '').split('/')[0] : ''; }

// Minimal markdown renderer for digests: ## / ### headings, **bold**, paragraphs.
function renderInline(text, keyBase) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) return <strong key={keyBase + '-' + i}>{p.slice(2, -2)}</strong>;
    return <span key={keyBase + '-' + i}>{p}</span>;
  });
}
function renderDigest(md) {
  if (!md) return <p className="crmint-md-p">Empty digest.</p>;
  const lines = md.replace(/\r/g, '').split('\n');
  const out = [];
  let para = [];
  const flush = (i) => {
    if (para.length) { out.push(<p className="crmint-md-p" key={'p' + i}>{renderInline(para.join(' '), 'p' + i)}</p>); para = []; }
  };
  lines.forEach((raw, i) => {
    const line = raw.trim();
    if (!line) { flush(i); return; }
    if (line.startsWith('### ')) { flush(i); out.push(<h4 className="crmint-md-h4" key={'h' + i}>{renderInline(line.slice(4), 'h' + i)}</h4>); return; }
    if (line.startsWith('## ')) { flush(i); out.push(<h3 className="crmint-md-h3" key={'h' + i}>{renderInline(line.slice(3), 'h' + i)}</h3>); return; }
    if (line.startsWith('# ')) { flush(i); out.push(<h3 className="crmint-md-h3" key={'h' + i}>{renderInline(line.slice(2), 'h' + i)}</h3>); return; }
    para.push(line);
  });
  flush('end');
  return out;
}

export default function CrmCompanyIntelligence({ companyId, clientId, layout = 'centre', initialTab, onSweepComplete }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(initialTab || 'overview');
  const [selectedDigestId, setSelectedDigestId] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editedDigest, setEditedDigest] = useState('');
  const [saving, setSaving] = useState(false);
  const [sweeping, setSweeping] = useState(false);
  const [sweepPrompt, setSweepPrompt] = useState('');
  const [showSendForm, setShowSendForm] = useState(false);
  const [sendForm, setSendForm] = useState({ to_email: '', to_name: '', from_name: '' });
  const [sending, setSending] = useState(false);
  const [feedFilter, setFeedFilter] = useState('all');
  const [feed, setFeed] = useState([]);
  const [feedLoading, setFeedLoading] = useState(false);

  const loadData = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await fetch(API_BASE + '/companies/' + companyId + '/intelligence');
      if (!res.ok) throw new Error('Failed to load');
      const json = await res.json();
      setData(json);
      if (json.digests && json.digests.length > 0) {
        setSelectedDigestId(prev => prev || json.digests[0].id);
      }
    } catch (e) {
      toast.error('Could not load intelligence: ' + e.message);
    }
    setLoading(false);
  }, [companyId]);

  useEffect(() => {
    setSelectedDigestId(null);
    setActiveTab(initialTab || 'overview');
    setEditing(false);
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, initialTab]);

  const loadFeed = useCallback(async () => {
    if (!clientId) return;
    setFeedLoading(true);
    try {
      const qs = feedFilter && feedFilter !== 'all' ? '?signal_type=' + feedFilter + '&limit=100' : '?limit=100';
      const res = await fetch(API_BASE + '/clients/' + clientId + '/intelligence/feed' + qs);
      if (res.ok) { const j = await res.json(); setFeed(j.feed || []); }
    } catch (_) {}
    setFeedLoading(false);
  }, [clientId, feedFilter]);

  useEffect(() => {
    if (activeTab === 'activity') loadFeed();
  }, [activeTab, loadFeed]);

  const selectedDigest = data?.digests?.find(d => d.id === selectedDigestId) || data?.digests?.[0];
  const digestText = selectedDigest ? (selectedDigest.edited_digest || selectedDigest.generated_digest || '') : '';

  async function handleRunSweep() {
    if (!companyId || sweeping) return;
    setSweeping(true);
    const toastId = toast.loading('Researching ' + (data?.company?.name || 'company') + '...');
    try {
      const res = await creditAwareFetch(API_BASE + '/clients/' + clientId + '/intelligence/sweep', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ company_ids: [companyId], prompt: sweepPrompt })
      });
      if (!res) { setSweeping(false); return; }
      if (!res.ok) {
        if (res.status === 499) { toast('Research stopped', { id: toastId, icon: '⏹️' }); setSweeping(false); return; }
        throw new Error(await res.text());
      }
      const newDigest = await res.json();
      toast.success('New digest generated', { id: toastId });
      setSweepPrompt('');
      await loadData();
      if (newDigest && newDigest.id) setSelectedDigestId(newDigest.id);
      setActiveTab('digest');
      if (onSweepComplete) onSweepComplete(newDigest);
    } catch (e) {
      toast.error('Research failed: ' + e.message, { id: toastId });
    }
    setSweeping(false);
  }

  async function stopSweep() {
    try {
      await fetch(API_BASE + '/jobs/stop', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind: 'digest', client_id: parseInt(clientId) })
      });
      toast('Stopping research...', { icon: '⏹️' });
    } catch (_) {}
  }

  async function handleSaveEdit() {
    if (!selectedDigest) return;
    setSaving(true);
    try {
      const res = await fetch(API_BASE + '/intelligence/' + selectedDigest.id, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ edited_digest: editedDigest })
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success('Digest saved');
      setEditing(false);
      await loadData();
    } catch (e) {
      toast.error('Could not save: ' + e.message);
    }
    setSaving(false);
  }

  async function handleSendDigest() {
    if (!sendForm.to_email || !selectedDigest) return;
    setSending(true);
    const toastId = toast.loading('Sending digest to ' + sendForm.to_email + '...');
    try {
      const res = await fetch(API_BASE + '/intelligence/' + selectedDigest.id + '/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sendForm)
      });
      if (!res.ok) {
        const errBody = await res.text();
        console.error('[send-digest] ' + res.status + ': ' + errBody);
        let userMessage = 'Could not send digest. Please try again or contact support.';
        if (res.status === 401 || res.status === 403) userMessage = 'Email service unavailable. Please contact support.';
        else if (res.status === 422 || res.status === 400) userMessage = 'Invalid email address. Please check and try again.';
        else if (res.status >= 500) userMessage = 'Email service is temporarily unavailable. Please try again in a moment.';
        throw new Error(userMessage);
      }
      toast.success('Digest sent to ' + sendForm.to_email, { id: toastId });
      setShowSendForm(false);
      await loadData();
    } catch (e) {
      toast.error(e.message, { id: toastId });
    }
    setSending(false);
  }

  if (!companyId) return <div className="crmint-empty">Select a company to view its intelligence</div>;
  if (loading) return <div className="crmint-empty">Loading intelligence...</div>;
  if (!data) return null;

  const { company, digests = [], signals = [], sweep_meta = {} } = data;
  const sweepStatus = sweep_meta.sweep_status || 'never';
  const statusColour = sweepStatus === 'fresh' ? GREEN : sweepStatus === 'due' ? '#F5A623' : '#64748B';
  const statusLabel = sweepStatus === 'fresh' ? 'Up to date' : sweepStatus === 'due' ? 'Due for refresh' : 'Never researched';
  const webUrl = company.website ? (company.website.startsWith('http') ? company.website : 'https://' + company.website) : null;
  const liUrl = company.linkedin_url ? (company.linkedin_url.startsWith('http') ? company.linkedin_url : 'https://' + company.linkedin_url) : null;
  const dom = favDomain(company.website);

  const sources = [];
  const seenUrls = new Set();
  signals.forEach(s => {
    if (s.source_url && !seenUrls.has(s.source_url)) {
      seenUrls.add(s.source_url);
      sources.push({ url: s.source_url, source: s.source, signal_type: s.signal_type, date: s.detected_at });
    }
  });

  const filteredSignals = feedFilter === 'all' ? signals : signals.filter(s => s.signal_type === feedFilter);
  const FILTER_TYPES = [{ key: 'all', label: 'All' }, ...Object.keys(SIGNAL).map(k => ({ key: k, label: SIGNAL[k].l }))];

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'digest', label: 'Digest' + (digests.length > 0 ? ' (' + digests.length + ')' : '') },
    { key: 'signals', label: 'Signals' + (signals.length > 0 ? ' (' + signals.length + ')' : '') },
    { key: 'sources', label: 'Sources' + (sources.length > 0 ? ' (' + sources.length + ')' : '') },
    { key: 'activity', label: 'Activity' }
  ];

  // ---- shared body pieces (used by both layouts) ----
  const overviewEl = (
    <div className="crmint-stack">
      <div className="crmint-stats">
        <StatCard label="Digests generated" value={digests.length} />
        <StatCard label="Signals detected" value={signals.length} />
        <StatCard label="Last researched" value={sweep_meta.last_swept_at ? relTime(sweep_meta.last_swept_at) : 'Never'} small />
      </div>
      {digests.length > 0 && (
        <div>
          <p className="crmint-sec">Latest digest</p>
          <div className="crmint-preview" onClick={() => setActiveTab('digest')}>
            <p className="crmint-preview-date">{ukDate(digests[0].created_at)}</p>
            <p className="crmint-preview-body">{(digests[0].edited_digest || digests[0].generated_digest || '').substring(0, 400)}</p>
            <p className="crmint-preview-more">Read full digest →</p>
          </div>
        </div>
      )}
      {digests.length === 0 && (
        <EmptyState title="No digest yet" message="Run Research now to generate the first AI intelligence digest for this company." />
      )}
    </div>
  );

  const digestEl = (
    <div>
      {digests.length === 0 ? (
        <EmptyState title="No digest yet" message="Generate the first AI digest with Research now. The AI researches recent signals, news, hiring, and changes for this company." />
      ) : (
        <>
          {digests.length > 1 && (
            <div className="crmint-histrow">
              <label className="crmint-histlabel">Viewing digest</label>
              <select className="crmint-select" value={selectedDigestId || ''} onChange={e => { setSelectedDigestId(parseInt(e.target.value)); setEditing(false); }}>
                {digests.map(d => (
                  <option key={d.id} value={d.id}>{ukDate(d.created_at)}{d.id === digests[0].id ? ' (latest)' : ''}{d.sent_status === 'sent' ? ' · sent' : ''}</option>
                ))}
              </select>
            </div>
          )}
          <div className="crmint-actionbar">
            {!editing ? (
              <>
                <button className="crmint-btn-ghost" onClick={() => { setEditedDigest(digestText); setEditing(true); }}>Edit</button>
                <button className="crmint-btn-send" onClick={() => setShowSendForm(true)}>{selectedDigest?.sent_status === 'sent' ? 'Send again' : 'Send to client'}</button>
                {selectedDigest?.sent_status === 'sent' && <span className="crmint-sent">✓ Sent</span>}
              </>
            ) : (
              <>
                <button className="crmint-btn-primary" onClick={handleSaveEdit} disabled={saving}>{saving ? 'Saving...' : 'Save changes'}</button>
                <button className="crmint-btn-ghost" onClick={() => setEditing(false)}>Cancel</button>
              </>
            )}
          </div>
          {showSendForm && (
            <div className="crmint-sendform">
              <p className="crmint-sendform-title">Send digest to client</p>
              <input type="email" placeholder="Client email" value={sendForm.to_email} onChange={e => setSendForm(p => ({ ...p, to_email: e.target.value }))} />
              <input type="text" placeholder="Recipient name (optional)" value={sendForm.to_name} onChange={e => setSendForm(p => ({ ...p, to_name: e.target.value }))} />
              <input type="text" placeholder="Your name (optional)" value={sendForm.from_name} onChange={e => setSendForm(p => ({ ...p, from_name: e.target.value }))} />
              <div className="crmint-sendform-actions">
                <button className="crmint-btn-send" onClick={handleSendDigest} disabled={sending || !sendForm.to_email}>{sending ? 'Sending...' : 'Send'}</button>
                <button className="crmint-btn-ghost" onClick={() => setShowSendForm(false)}>Cancel</button>
              </div>
            </div>
          )}
          {editing ? (
            <textarea className="crmint-editor" value={editedDigest} onChange={e => setEditedDigest(e.target.value)} />
          ) : (
            <div className="crmint-digestbody">
              <p className="crmint-digestbody-date">Generated {ukDate(selectedDigest?.created_at)}</p>
              <div className="crmint-digesttext">{renderDigest(digestText)}</div>
            </div>
          )}
        </>
      )}
    </div>
  );

  const signalsEl = (
    <div>
      {signals.length === 0 ? (
        <EmptyState title="No signals yet" message="Run research, or wait for the daily sweep to find new signals." />
      ) : (
        <div className="crmint-siglist">{signals.map(s => <SignalCard key={s.id} signal={s} expanded />)}</div>
      )}
    </div>
  );

  const sourcesEl = (
    <div>
      {sources.length === 0 ? (
        <EmptyState title="No sources yet" message="Sources appear here as the AI gathers intelligence from across the web." />
      ) : (
        <div className="crmint-siglist">
          {sources.map((src, i) => {
            const m = SIGNAL[src.signal_type] || SIGNAL.news;
            return (
              <a key={i} href={src.url} target="_blank" rel="noreferrer" className="crmint-source">
                <div className="crmint-source-top">
                  <span className="crmint-pill" style={{ color: m.c, background: m.bg }}>{SIGNAL_LABELS[src.signal_type] || src.signal_type}</span>
                  <span className="crmint-source-date">{ukDate(src.date)}</span>
                </div>
                <p className="crmint-source-name">{src.source}</p>
                <p className="crmint-source-url">{src.url}</p>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );

  const activityEl = (
    <div className="crmint-activity">
      <div className="crmint-feedfilters crmint-activity-filters">
        {FILTER_TYPES.map(f => (
          <button key={f.key} onClick={() => setFeedFilter(f.key)}
            className={'crmint-fpill' + (feedFilter === f.key ? ' crmint-fpill-on' : '')}
            style={feedFilter === f.key && f.key !== 'all' ? { background: (SIGNAL[f.key] || SIGNAL.news).c, borderColor: (SIGNAL[f.key] || SIGNAL.news).c, color: '#fff' } : undefined}>
            {f.label}
          </button>
        ))}
      </div>
      {feedLoading ? (
        <div className="crmint-empty">Loading activity...</div>
      ) : feed.length === 0 ? (
        <EmptyState title="No activity yet" message="Signals and digests across all companies appear here as research runs." />
      ) : (
        groupByDate(feed).map(group => (
          <div className="crmint-feedgroup" key={group.label}>
            <p className="crmint-feedgroup-label">{group.label}</p>
            <div className="crmint-siglist">
              {group.items.map(item => <FeedCard key={item.feed_type + '-' + item.id} item={item} />)}
            </div>
          </div>
        ))
      )}
    </div>
  );

  const leftBody = activeTab === 'overview' ? overviewEl : activeTab === 'digest' ? digestEl : activeTab === 'signals' ? signalsEl : activeTab === 'sources' ? sourcesEl : activityEl;

  const tabsEl = (
    <div className="crmint-tabs">
      {tabs.map(tab => (
        <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={'crmint-tab' + (activeTab === tab.key ? ' crmint-tab-on' : '')}>{tab.label}</button>
      ))}
    </div>
  );

  // ===================== RECORD LAYOUT (HubSpot match) =====================
  if (layout === 'record') {
    return (
      <div className="crmint crmint-record">
        {/* header strip */}
        <div className="crmint-rhead">
          <div className="crmint-rhead-id">
            {dom ? <img className="crmint-rhead-fav" src={`https://www.google.com/s2/favicons?domain=${dom}&sz=64`} alt="" /> : <span className="crmint-rhead-favph">{(company.name || '?').slice(0,2).toUpperCase()}</span>}
            <div>
              <h2 className="crmint-rhead-name">{company.name}</h2>
              {webUrl && <a className="crmint-rhead-web" href={webUrl} target="_blank" rel="noreferrer">{dom} ↗</a>}
            </div>
          </div>
          <div className="crmint-rhead-stats">
            {company.size && <div className="crmint-rhead-stat"><span className="crmint-rhead-statl">Employees</span><span className="crmint-rhead-statv">{company.size}</span></div>}
            {company.revenue && <div className="crmint-rhead-stat"><span className="crmint-rhead-statl">Revenue</span><span className="crmint-rhead-statv">{company.revenue}</span></div>}
            {company.location && <div className="crmint-rhead-stat"><span className="crmint-rhead-statl">Location</span><span className="crmint-rhead-statv">{company.location}</span></div>}
          </div>
          <div className="crmint-rhead-social">
            {liUrl && <a href={liUrl} target="_blank" rel="noreferrer" className="crmint-rhead-soc">in</a>}
          </div>
        </div>

        {/* two columns */}
        <div className="crmint-rcols">
          {/* LEFT — Data Agent */}
          <div className="crmint-rcol crmint-rcol-left">
            <div className="crmint-panelhead">
              <div>
                <span className="crmint-panelh-title">Intelligence Agent</span>
                <span className="crmint-panelh-sub">AI research, digests, and client-ready reports for {company.name}.</span>
              </div>
              <div className="crmint-panelh-actions">
                <span className="crmint-status" style={{ background: statusColour + '18', color: statusColour }}><span className="crmint-dot" style={{ background: statusColour }} />{statusLabel}</span>
                <button className="crmint-btn-primary" onClick={handleRunSweep} disabled={sweeping}>{sweeping ? 'Researching...' : digests.length > 0 ? 'Refresh intelligence' : 'Research now'}</button>
                {sweeping && <button className="crmint-btn-stop" onClick={stopSweep}>Stop</button>}
              </div>
            </div>
            {tabsEl}
            <div className="crmint-rcol-body">{leftBody}</div>
          </div>

          {/* RIGHT — Buyer Intent / Signals */}
          <div className="crmint-rcol crmint-rcol-right">
            <div className="crmint-panelhead">
              <div>
                <span className="crmint-panelh-title">Buyer Intent</span>
                <span className="crmint-panelh-sub">{signals.length} live signal{signals.length === 1 ? '' : 's'} detected</span>
              </div>
            </div>
            <div className="crmint-feedfilters">
              {FILTER_TYPES.map(f => (
                <button key={f.key} onClick={() => setFeedFilter(f.key)}
                  className={'crmint-fpill' + (feedFilter === f.key ? ' crmint-fpill-on' : '')}
                  style={feedFilter === f.key && f.key !== 'all' ? { background: (SIGNAL[f.key] || SIGNAL.news).c, borderColor: (SIGNAL[f.key] || SIGNAL.news).c, color: '#fff' } : undefined}>
                  {f.label}
                </button>
              ))}
            </div>
            <div className="crmint-rcol-body">
              {filteredSignals.length === 0 ? (
                <EmptyState title={feedFilter === 'all' ? 'No signals yet' : 'No ' + (SIGNAL[feedFilter]?.l || feedFilter).toLowerCase() + ' signals'} message="Signals appear here from the daily sweep or after running research." />
              ) : (
                groupByDate(filteredSignals).map(group => (
                  <div className="crmint-feedgroup" key={group.label}>
                    <p className="crmint-feedgroup-label">{group.label}</p>
                    <div className="crmint-siglist">{group.items.map(s => <SignalCard key={s.id} signal={s} expanded />)}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===================== CENTRE LAYOUT (cockpit) =====================
  return (
    <div className="crmint crmint-centre">
      <div className="crmint-head">
        <div className="crmint-head-main">
          <h2 className="crmint-coname">{company.name}</h2>
          <div className="crmint-meta">
            {company.size && <span>{company.size}</span>}
            {company.location && <span>{company.size ? '· ' : ''}{company.location}</span>}
            {parseSectors(company.sector).length > 0 && <span className="crmint-secpills">{parseSectors(company.sector).map((s, i) => { const SEC = ['#5B3FD6','#0B5BB5','#056F4E','#B25400','#066576','#A21C5A','#3F4956','#7A4D00']; let h = 0; for (let k = 0; k < s.length; k++) h = (h * 31 + s.charCodeAt(k)) >>> 0; const c = SEC[h % SEC.length]; return <span key={i} className="crmint-secpill" style={{ color: c, background: c + '1A' }}>{s}</span>; })}</span>}
            {webUrl && <a href={webUrl} target="_blank" rel="noreferrer">Website ↗</a>}
            {liUrl && <a href={liUrl} target="_blank" rel="noreferrer">LinkedIn ↗</a>}
          </div>
          {company.description && <p className="crmint-desc">{company.description}</p>}
        </div>
        <div className="crmint-head-actions">
          <span className="crmint-status" style={{ background: statusColour + '18', color: statusColour }}><span className="crmint-dot" style={{ background: statusColour }} />{statusLabel}</span>
          <button className="crmint-btn-primary" onClick={handleRunSweep} disabled={sweeping}>{sweeping ? 'Researching...' : digests.length > 0 ? 'Refresh intelligence' : 'Research now'}</button>
          {sweeping && <button className="crmint-btn-stop" onClick={stopSweep}>Stop research</button>}
        </div>
      </div>
      {tabsEl}
      <div className="crmint-centrebody">{leftBody}</div>
    </div>
  );
}

function FeedCard({ item }) {
  if (item.feed_type === 'digest') {
    const names = item.company_names || [];
    const headline = names.length === 1 ? names[0] : (names.length || 'Several') + ' companies';
    const summary = (item.edited_digest || item.generated_digest || '').replace(/[#*]/g, '').slice(0, 180);
    return (
      <div className="crmint-feedcard crmint-feedcard-digest">
        <div className="crmint-feedcard-top">
          <span className="crmint-feedcard-type" style={{ color: '#FF7A59' }}>◆ Digest</span>
          <span className="crmint-feedcard-date">{relTime(item.created_at)}</span>
        </div>
        <p className="crmint-feedcard-co">{headline}</p>
        <p className="crmint-feedcard-summary">{summary}</p>
        {item.sent_status === 'sent' && <span className="crmint-feedcard-sent">✓ Sent</span>}
      </div>
    );
  }
  const m = SIGNAL[item.signal_type] || SIGNAL.news;
  return (
    <div className="crmint-feedcard" style={{ background: m.bg, borderLeft: '3px solid ' + m.c }}>
      <div className="crmint-feedcard-top">
        <span className="crmint-feedcard-type" style={{ color: m.c }}>{m.icon} {SIGNAL_LABELS[item.signal_type] || item.signal_type}</span>
        <span className="crmint-feedcard-date">{relTime(item.detected_at || item.created_at)}</span>
      </div>
      <p className="crmint-feedcard-co">{item.company_name}</p>
      <p className="crmint-feedcard-summary">{item.signal_data?.summary}</p>
      {item.source && <a className="crmint-feedcard-src" href={item.source_url || undefined} target="_blank" rel="noreferrer">{item.source}{item.source_url ? ' ↗' : ''}</a>}
    </div>
  );
}

function StatCard({ label, value, small }) {
  return (
    <div className="crmint-statcard">
      <p className="crmint-statcard-label">{label}</p>
      <p className={'crmint-statcard-value' + (small ? ' crmint-statcard-small' : '')}>{value}</p>
    </div>
  );
}

function SignalCard({ signal, expanded }) {
  const m = SIGNAL[signal.signal_type] || SIGNAL.news;
  return (
    <div className="crmint-sigcard" style={{ background: m.bg, borderLeft: '3px solid ' + m.c }}>
      <div className="crmint-sigcard-top">
        <span className="crmint-sigcard-type" style={{ color: m.c }}>{m.icon} {SIGNAL_LABELS[signal.signal_type] || signal.signal_type}</span>
        <span className="crmint-sigcard-date">{ukDate(signal.detected_at)}</span>
      </div>
      <p className="crmint-sigcard-summary">{signal.signal_data?.summary}</p>
      <div className="crmint-sigcard-foot">
        {signal.source_url && <a href={signal.source_url} target="_blank" rel="noreferrer" className="crmint-sigcard-src">{signal.source} ↗</a>}
        {expanded && signal.relevance_score != null && <span className="crmint-sigcard-score" style={{ color: m.c }}>Score {signal.relevance_score}/10</span>}
      </div>
    </div>
  );
}

function EmptyState({ title, message }) {
  return (
    <div className="crmint-emptystate">
      <p className="crmint-emptystate-title">{title}</p>
      <p className="crmint-emptystate-msg">{message}</p>
    </div>
  );
}
